<script setup lang="ts">
import {
    beneficialOwnerEditingIsLocked,
    useLegalAccountDataState,
} from '../../../../utilities/account/useAccountDataLegal'
import { MailIcon } from '@heroicons/vue/solid'
import { UserIcon } from '@heroicons/vue/outline'

import { computed } from 'vue'
import AccountLegalAddBeneficialOwnerForm from '../AccountLegalAddBeneficialOwnerForm.vue'
import AccountLegalSignaturesPreview from '../AccountLegalSignaturesPreview.vue'
import BeneficialOwnerListItem from '../beneficialOwners/BeneficialOwnerListItem.vue'
const state = useLegalAccountDataState()

const managers = computed(() => {
    return state?.legalProfile?.managers.filter((manager) => manager.is_beneficial_owner)
})
</script>

<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-8 px-4 py-5 sm:px-6">
        <div class="overflow-hidden border border-gray-200 bg-white shadow sm:col-span-1 sm:rounded-md">
            <ul
                v-if="managers.length > 0 || state.legalProfile.beneficial_owners.length > 0"
                role="list"
                class="divide-y divide-gray-200">
                <li v-for="manager in managers" :key="manager.email" class="mb-0">
                    <div>
                        <div class="flex items-center px-2 py-3 sm:px-4">
                            <div class="flex min-w-0 flex-1 items-center">
                                <div class="flex-shrink-0">
                                    <div
                                        class="flex items-center justify-center rounded-full border-2 border-gray-300 bg-white p-3">
                                        <UserIcon class="border-gray-150 w-6" />
                                    </div>
                                </div>
                                <div class="min-w-0 flex-1 px-4">
                                    <div>
                                        <p class="text-sm font-medium text-gray-900">
                                            <span class="truncate">
                                                {{ manager.first_name + ' ' + manager.last_name }}
                                            </span>
                                            <span
                                                v-if="manager.is_beneficial_owner"
                                                class="ml-1 inline-flex items-center rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-800"
                                                >{{ $t('legal_account_beneficial_owners.manager') }}</span
                                            >
                                        </p>
                                        <p class="mt-2 flex items-center text-sm text-gray-500">
                                            <MailIcon
                                                class="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                                                aria-hidden="true" />
                                            <span class="truncate">{{ manager.email }}</span>
                                        </p>
                                    </div>
                                </div>
                                <div class="hidden divide-y md:block">
                                    <AccountLegalSignaturesPreview
                                        :signatures="manager.signatures"></AccountLegalSignaturesPreview>
                                </div>
                            </div>
                        </div>
                    </div>
                </li>
                <li
                    v-for="beneficialOwner in state.legalProfile.beneficial_owners"
                    :key="beneficialOwner.email"
                    class="mb-0">
                    <BeneficialOwnerListItem :beneficial-owner="beneficialOwner" />
                </li>
            </ul>
            <div v-else class="py-4 text-center">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>

                <h3 class="mt-2 text-sm font-medium text-gray-900">
                    {{ $t('beneficial_owner.no_beneficial_owners') }}
                </h3>
                <p class="mt-1 text-sm text-gray-500">{{ $t('beneficial_owner.no_beneficial_owners_description') }}</p>
            </div>
        </div>

        <div v-if="!beneficialOwnerEditingIsLocked()" class="border-t border-gray-200 pt-6">
            <h3 class="text-lg font-bold">
                {{ $t('legal_account_beneficial_owners.add_beneficial_owner') }}
            </h3>

            <AccountLegalAddBeneficialOwnerForm></AccountLegalAddBeneficialOwnerForm>
        </div>
    </div>
</template>
